import { useSearch } from "@tanstack/react-router";
import { CareerPathCompare } from "@/components/career-path/CareerPathCompare";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { PageHeader } from "@/components/shared/page-header";

/**
 * Search-param seeds for the comparison. A person profile's "Compare roles"
 * button passes the viewed person's primary role as `startRole` (plus their
 * name, for the hint under the picker) so the page starts from *their* role
 * rather than the signed-in viewer's. The Role Catalog's per-row compare
 * button passes the clicked role as `targetRole`. Either side absent = the
 * page's own default (viewer's primary role / empty target).
 */
interface RoleCompareSearch {
  startRole?: string;
  startPerson?: string;
  targetRole?: string;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

/** `validateSearch` for the compare route — keep well-formed seeds, drop the rest. */
export function parseRoleCompareSearch(search: Record<string, unknown>): RoleCompareSearch {
  const result: RoleCompareSearch = {};
  const startRole = str(search.startRole);
  if (startRole) {
    result.startRole = startRole;
    result.startPerson = str(search.startPerson);
  }
  const targetRole = str(search.targetRole);
  if (targetRole) result.targetRole = targetRole;
  return result;
}

export function RoleComparePage() {
  const { startRole, startPerson, targetRole } = parseRoleCompareSearch(
    useSearch({ strict: false }),
  );
  useSetBreadcrumbs([{ label: "Roles", to: "/dashboard/roles" }, { label: "Compare Roles" }]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Roles"
        title="Compare Roles"
        description="Compare your current role to a target role to see which requirements overlap, which you'd need to gain, and which are no longer needed."
      />
      <CareerPathCompare
        startRoleId={startRole}
        startPersonName={startPerson}
        targetRoleId={targetRole}
      />
    </div>
  );
}
